// features/recommendations/espDashboardSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchEspDashboardDataService } from '../../services/espDashboards/EspDashboardsService'; // service import
import { OptimizationSummaryItem } from '../../types/GLDashboardTypes';

export interface RecommendationGridState<T = any> {
  data: T[];
}

export interface EspDashboardState {
  frequencyChange: RecommendationGridState;
  espPumpChange: RecommendationGridState;
  productionTop5: RecommendationGridState;
  productionBottom5: RecommendationGridState;
  recentTests: RecommendationGridState;
  espsInRange: RecommendationGridState;
  degradationSummary: RecommendationGridState;
  drawdownSummary: RecommendationGridState;
  optimizationOpportunities: RecommendationGridState;
  count: Record<string, number>;
  paretoCounts: Record<string, number>;
  chartData: any[];
  HighDaysSinceTested: number;
  degradationWellsAffected: number;
  optimizationData: OptimizationSummaryItem[];
  loading: boolean;
  error?: string | null;
}

const initialState: EspDashboardState = {
  frequencyChange: { data: [] },
  espPumpChange: { data: [] },
  productionTop5: { data: [] },
  productionBottom5: { data: [] },
  recentTests: { data: [] },
  espsInRange: { data: [] },
  degradationSummary: { data: [] },
  drawdownSummary: { data: [] },
  optimizationOpportunities: { data: [] },
  count: {},
  paretoCounts: {},
  chartData: [],
  HighDaysSinceTested: 0,
  degradationWellsAffected: 0,
  optimizationData: [],
  loading: false,
  error: null,
};

/**
 * Thunk: fetchESPDashboard
 * Calls the endpoint added :
 * GET /pumpchecker/Checker/Well/DashboardData
 */
export const fetchESPDashboard = createAsyncThunk(
  'espDashboard/fetch',
  async (
    params:
      | { areaIds?: string[]; businessUnitIds?: string[]; fieldIds?: string[]; wellIds?: string[] | string }
      | undefined,
    thunkAPI,
  ) => {
    try {
      // Use the plain service function (not another thunk)
      const data = await fetchEspDashboardDataService(params);
      return data;
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to fetch ESP dashboard data';
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const espDashboardSlice = createSlice({
  name: 'espDashboard',
  initialState,
  reducers: {
    clearEspDashboardError(state) {
      state.error = null;
    },
    setFrequencyChange(state, action: PayloadAction<any[]>) {
      state.frequencyChange.data = action.payload ?? [];
    },
    setEspPumpChange(state, action: PayloadAction<any[]>) {
      state.espPumpChange.data = action.payload ?? [];
    },
    setProductionTop5(state, action: PayloadAction<any[]>) {
      state.productionTop5.data = action.payload ?? [];
    },
    setProductionBottom5(state, action: PayloadAction<any[]>) {
      state.productionBottom5.data = action.payload ?? [];
    },
    setRecentTests(state, action: PayloadAction<any[]>) {
      state.recentTests.data = action.payload ?? [];
    },
    setEspsInRange(state, action: PayloadAction<any[]>) {
      state.espsInRange.data = action.payload ?? [];
    },
    setDegradationSummary(state, action: PayloadAction<any[]>) {
      state.degradationSummary.data = action.payload ?? [];
    },
    setDrawdownSummary(state, action: PayloadAction<any[]>) {
      state.drawdownSummary.data = action.payload ?? [];
    },
    setOptimizationOpportunities(state, action: PayloadAction<any[]>) {
      state.optimizationOpportunities.data = action.payload ?? [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchESPDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchESPDashboard.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;

        const payload = action.payload || {};

        // Defensive mapping — update if your API uses different keys/nesting.
        state.frequencyChange.data =
          payload?.ChangeFrequencyTop5 ??
          payload?.frequencyChangeTop5 ??
          payload?.FrequencyChangeTop5 ??
          payload?.frequencyChange ??
          [];

        state.espPumpChange.data =
          payload?.ChangePumpTop5 ?? payload?.changePumpTop5 ?? payload?.espPumpChange ?? payload?.pumpChangeTop5 ?? [];

        state.productionTop5.data =
          payload?.ProductionTop5 ??
          payload?.productionTop5 ??
          payload?.Top5Producers ??
          payload?.productionTop5Data ??
          [];

        state.productionBottom5.data =
          payload?.ProductionBottom5 ??
          payload?.productionBottom5 ??
          payload?.Bottom5Producers ??
          payload?.productionBottom5Data ??
          [];

        state.recentTests.data = payload?.RecentTestsData ?? [];
        state.espsInRange.data = payload?.EspsInRangeData ?? [];
        state.degradationSummary.data = payload?.DegradationSummaryData ?? [];
        state.drawdownSummary.data = payload?.DrawdownSummaryData ?? [];

        state.count = {
          WellsEnabled: action.payload.WellsEnabled,
          WellsRunning: action.payload.WellsRunning,
          WellsNoTests: action.payload.WellsNoTests,
          CountHighDaysSinceTested: action.payload.WellsOver30,
          WellsOutOfService: action.payload.DegradationLostOil,
        };
        state.HighDaysSinceTested = action.payload.HighDaysSinceTested;
        state.degradationWellsAffected = action.payload.DegradationWellsAffected || 0;
        state.paretoCounts = {
          ParetoPercOver90: action.payload.ParetoPercOver90,
          ParetoPercWells80: action.payload.ParetoPercWells80,
          ParetoPercWells80To90: action.payload.ParetoPercWells80To90,
          ParetoCountWells80: action.payload.ParetoCountWells80,
          ParetoCountWells80To90: action.payload.ParetoCountWells80To90,
          ParetoCountWellsOver90: action.payload.ParetoCountWellsOver90,
        };
        state.chartData = action.payload.FieldPotentialData || [];

        // Transform optimizationOpportunitiesData for OpportunitiesChart
        const rawOpportunities = payload?.OptimizationOpportunitiesData;
        const toXYArray = (arr: any[] | undefined) => (Array.isArray(arr) ? arr.map(({ X, Y }) => [X, Y]) : []);
        const toWellsArray = (arr: any[] | undefined) =>
          Array.isArray(arr)
            ? arr.map(({ X, Y, Name, Color, WellId }) => ({ x: X, y: Y, name: Name, color: Color, wellId: WellId }))
            : [];

        const transformedOpportunities =
          rawOpportunities && typeof rawOpportunities === 'object'
            ? {
                MaxControl: toXYArray(rawOpportunities.MaxFreqData),
                MinControl: toXYArray(rawOpportunities.MinPipData),
                Wells: toWellsArray(rawOpportunities.WellsData),
              }
            : { MaxControl: [], MinControl: [], Wells: [] };

        state.optimizationOpportunities.data = [transformedOpportunities];
        state.optimizationData = [
          {
            text: 'Speed change',
            wellcount: action.payload.FrequencyChangeWellsCount,
            value: action.payload.FrequencyChangeOilAdditional,
            navigateState: { tab: 'esp', widget: 'espRecommendationsFrequencyChange' },
          },
          {
            text: 'Pump change',
            wellcount: action.payload.PumpChangeWellsCount,
            value: action.payload.PumpChangeOilAdditional,
            navigateState: { tab: 'esp', widget: 'espRecommendationsPumpChange' },
          },
        ];
      })
      .addCase(fetchESPDashboard.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch ESP dashboard data';
      });
  },
});

export const {
  clearEspDashboardError,
  setFrequencyChange,
  setEspPumpChange,
  setProductionTop5,
  setProductionBottom5,
  setRecentTests,
  setEspsInRange,
  setDegradationSummary,
  setDrawdownSummary,
  setOptimizationOpportunities,
} = espDashboardSlice.actions;

export default espDashboardSlice.reducer;

// Selectors
export const selectEspDashboardLoading: (state: any) => boolean = (state) => state.espDashboard?.loading ?? false;
export const selectEspDashboardError = (state: any) => state.espDashboard?.error ?? null;
export const selectEspFrequencyChange = (state: any) => state.espDashboard?.frequencyChange?.data ?? [];
export const selectEspPumpChange = (state: any) => state.espDashboard?.espPumpChange?.data ?? [];
export const selectEspProductionTop5 = (state: any) => state.espDashboard?.productionTop5?.data ?? [];
export const selectEspProductionBottom5 = (state: any) => state.espDashboard?.productionBottom5?.data ?? [];
export const selectEspRecentTests = (state: any) => state.espDashboard?.recentTests?.data ?? [];
export const selectEspsInRange = (state: any) => state.espDashboard?.espsInRange?.data ?? [];
export const selectDegradationSummary = (state: any) => state.espDashboard?.degradationSummary?.data ?? [];
export const selectDrawdownSummary = (state: any) => state.espDashboard?.drawdownSummary?.data ?? [];
export const selectOptimizationOpportunities = (state: any) =>
  state.espDashboard?.optimizationOpportunities?.data?.[0] || {};
export const selectESPSummaryCount = (state: any) => state.espDashboard?.count;
export const selectESPParetoCounts = (state: any) => state.espDashboard?.paretoCounts;
export const selectESPChartData = (state: any) => state.espDashboard?.chartData;
//export const selectESPDegradationLostOil = (state: any) => state.espDashboard?.DegradationLostOil || 0;
export const selectESPDegradationWellsAffected = (state: any) => state.espDashboard?.degradationWellsAffected || 0;
export const selectEspOptimizationData = (state: any) => state.espDashboard?.optimizationData ?? [];
